import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import LinkWithIconComponent from '../LinkWithIcon/LinkWithIconComponent';
import Image from '../../Image/Image';

// R2 SVG URLs
const TICK_ICON = 'https://assets.askhippo.co.za/Icons/common/TickCircle.svg';
const CROSS_ICON =
  'https://assets.askhippo.co.za/Icons/common/Delete-Square-New.svg';
const CROSS_ICON_BLACK =
  'https://assets.askhippo.co.za/Icons/common/Delete-Square-New-Black.svg';
const ARROW_ICON = 'https://assets.askhippo.co.za/Icons/common/IconArrow.svg';

const TEXT_DARK = '#162F3B';
const TEXT_GREY = '#A2ACB1';

const normalizeIconType = (type) => String(type || '').toLowerCase();

const getListItemColor = (type, crossColourBlack) => {
  const normalized = normalizeIconType(type);

  if (normalized === 'cross' || normalized === 'untick') {
    return crossColourBlack ? TEXT_DARK : TEXT_GREY;
  }

  return TEXT_DARK;
};

const IconSelector = ({ type, crossColourBlack }) => {
  const normalizedType = normalizeIconType(type);

  if (normalizedType === 'tick') {
    return (
      <Image
        src={TICK_ICON}
        alt="Yes"
        width={24}
        height={24}
        sx={{ width: 24, height: 24, flexShrink: 0 }}
      />
    );
  }
  if (normalizedType === 'cross' || normalizedType === 'untick') {
    return (
      <Image
        src={crossColourBlack ? CROSS_ICON_BLACK : CROSS_ICON}
        alt="No"
        width={24}
        height={24}
        sx={{ width: 24, height: 24, flexShrink: 0 }}
      />
    );
  }
  return null;
};

const ThreeColumnCardMobileComponent = ({ data }) => {
  const { title, description, footer, cards, footerLink, crossColourBlack } =
    data;
  const navigate = useNavigate();
  const buttonRefs = useRef([]);
  const cardRefs = useRef([]);
  const [equalCardHeight, setEqualCardHeight] = useState(null);
  const hasFooter = Boolean(String(footer || '').trim());

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let rafId = null;

    const updateCardHeights = () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }

      rafId = window.requestAnimationFrame(() => {
        const cardHeights = cardRefs.current
          .map((card) => {
            if (!card || !card.getBoundingClientRect) return 0;

            return card.getBoundingClientRect().height || 0;
          })
          .filter(Boolean);

        if (!cardHeights.length) return;

        const nextHeight = Math.ceil(Math.max(...cardHeights));
        setEqualCardHeight((currentHeight) =>
          currentHeight === nextHeight ? currentHeight : nextHeight
        );
      });
    };

    updateCardHeights();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            updateCardHeights();
          })
        : null;

    cardRefs.current.forEach((card) => {
      if (card && resizeObserver) {
        resizeObserver.observe(card);
      }
    });

    window.addEventListener('resize', updateCardHeights);

    return () => {
      window.removeEventListener('resize', updateCardHeights);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [cards]);

  // Bounce animation (resolves when complete)
  const gs_bounce = (target, strength = 0.85) => {
    if (!target) return Promise.resolve();
    return new Promise((resolve) => {
      const tl = gsap.timeline({
        onComplete: resolve,
        defaults: { transformOrigin: '50% 50%' },
      });
      tl.to(target, {
        scale: strength,
        duration: 0.15,
        ease: 'power3.inOut',
      }).to(target, {
        scale: 1,
        duration: 0.85,
        ease: 'elastic.out(1.75, 0.4)',
      });
    });
  };

  const handleClick = async (index, link) => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReduced) {
      await gs_bounce(buttonRefs.current[index]);
    }
    if (link) navigate(link);
  };

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        maxWidth: '100vw',
        backgroundColor: '#E3E6E8',
        borderRadius: '34px',
        px: '16px',
        pt: '32px',
        pb: '32px',
        boxSizing: 'border-box',
        textAlign: 'center',

        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Typography
          component={data.component || 'h2'}
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '2rem',
            lineHeight: '2.25rem',
            letterSpacing: '-0.03em',
            color: '#162F3B',
            whiteSpace: 'pre-line',
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '1.25rem',
            lineHeight: '1.5rem',
            letterSpacing: '-0.03em',
            color: '#162F3B',
            px: '16px',
            alignSelf: 'stretch',
            boxSizing: 'border-box',
            whiteSpace: 'pre-line',
          }}
        >
          {description}
        </Typography>
      </Box>

      {/* Cards */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          mt: '40px',
        }}
      >
        {cards.map((card, index) => (
          <ScrollFadeWrapper key={index} delay={index * 0.1}>
            <Box
              ref={(el) => (cardRefs.current[index] = el)}
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '34px',
                minHeight: equalCardHeight ? `${equalCardHeight}px` : 'auto',
                boxShadow:
                  '0px 6px 16px 0px #162F3B1F, 0px 1px 2px 0px #A2ACB11F',
                px: '24px',
                pt: '56px',
                pb: '40px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
              }}
            >
              <Typography
                component={card.component || 'h3'}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  lineHeight: '1.5rem',
                  letterSpacing: '-0.03em',
                  color: '#162F3B',
                  textAlign: 'left',
                }}
              >
                {card.title}
              </Typography>

              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                  letterSpacing: '-0.02em',
                  color: '#162F3B',
                  mb: '40px',
                  textAlign: 'left',
                }}
              >
                {card.body}
              </Typography>

              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
              >
                {card.listItems.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <IconSelector
                      type={item.icon}
                      crossColourBlack={crossColourBlack}
                    />
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.02em',
                        color: getListItemColor(item.icon, crossColourBlack),
                        textAlign: 'left',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {card.buttonText && (
                <Box
                  sx={{ mt: '8px', display: 'flex', justifyContent: 'center' }}
                >
                  <Button
                    ref={(el) => (buttonRefs.current[index] = el)}
                    variant="contained"
                    endIcon={
                      <Image
                        src={ARROW_ICON}
                        alt="Compare quotes"
                        width={24}
                        height={24}
                        sx={{ width: 24, height: 24 }}
                      />
                    }
                    onClick={() => handleClick(index, card.link)}
                    sx={{
                      width: 'fit-content',
                      minWidth: 0,
                      height: '48px',
                      borderRadius: '24px',
                      backgroundColor: '#C7CA00',
                      color: '#162F3B',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.03em',
                      textTransform: 'none',
                      willChange: 'transform',
                      display: 'flex',
                      alignSelf: 'center',
                      px: '24px',
                      '&:hover': { backgroundColor: '#b7bc00' },
                      '& .MuiButton-endIcon': { marginLeft: '8px' },
                    }}
                  >
                    {card.buttonText}
                  </Button>
                </Box>
              )}
            </Box>
          </ScrollFadeWrapper>
        ))}
      </Box>

      {hasFooter ? (
        <LinkWithIconComponent
          href={footerLink || undefined}
          iconSize="1rem"
          sx={{
            mt: '40px',
            width: '100%',
            px: '16px',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '1.5rem',
            letterSpacing: '-0.02em',
            color: '#162F3B',
            textAlign: 'center',
          }}
        >
          {footer?.replace('{CURRENT_YEAR}', new Date().getFullYear())}
        </LinkWithIconComponent>
      ) : null}
    </ScrollFadeSection>
  );
};

export default ThreeColumnCardMobileComponent;
